import type { OperationFinancialInstrumentEnumFR } from './enums/OperationFinancialInstrumentEnum';
import {
	getOperationFinancialInstrumentFromFR,
	OperationFinancialInstrumentEnum
} from './enums/OperationFinancialInstrumentEnum';
import type { TargetTypeEnumFR } from './enums/TargetTypeEnum';
import { getTargetTypeFromFR, TargetTypeEnum } from './enums/TargetTypeEnum';
import type { OperationCategoryEnum, OperationCategoryEnumFR } from './enums/OperationCategoryEnum';
import { getOperationCategoryFromFR } from './enums/OperationCategoryEnum';
import type { PartnershipLabelEnum, PartnershipLabelEnumFR } from './enums/PartnershipLabelEnum';
import { getPartnershipLabelFromFR } from './enums/PartnershipLabelEnum';
import type { CompanyModel, CompanyModelFR } from './CompanyModel';
import { translateCompany } from './CompanyModel';
import type { OperationOutingsEnum, OperationOutingsEnumFR } from './enums/OperationOutingsEnum';
import { translateOperationOutings } from './enums/OperationOutingsEnum';
import type {
	OperationLegalStatusEnum,
	OperationLegalStatusEnumFR
} from './enums/OperationLegalStatusEnum';
import { translateOperationLegalStatus } from './enums/OperationLegalStatusEnum';
import type { ShareTypeEnum, ShareTypeEnumFR } from './enums/ShareTypeEnum';
import { translateShareTypeEnum } from './enums/ShareTypeEnum';
import type { FileModel, FileModelFR } from './FileModel';
import { translateFileModel, translateFileModelArray } from './FileModel';
import type { ImageModel, ImageModelFR } from './ImageModel';
import { translateImageModel, translateImageModelArray } from './ImageModel';
import type { AddressModel, AddressModelFR } from './AddressModel';
import { translateAddressModel } from './AddressModel';
import type { TeamMemberModel, TeamMemberModelFR } from './TeamMemberModel';
import { translateTeamMemberArray } from './TeamMemberModel';
import type { DevelopmentStepModel, DevelopmentStepModelFR } from './DevelopmentStepModel';
import { translateDevelopmentStepArray } from './DevelopmentStepModel';
import type { FundUsageModel, FundUsageModelFR } from './FundUsageModel';
import { translateFundUsageModelArray } from './FundUsageModel';
import type { SubscriptionOfferModel, SubscriptionOfferModelFR } from './SubscriptionOfferModel';
import { translateSubscriptionOfferModel } from './SubscriptionOfferModel';
import type { OperationTrancheModel, OperationTrancheResponse } from './OperationTrancheModel';
import { translateOperationTrancheResponseArray } from './OperationTrancheModel';
import type { StatsVotesModel, StatsVotesModelFR } from './StatsVotesModel';
import { translateStatsVotesModel } from './StatsVotesModel';
import type { ProjectManager, ProjectManagerFR } from './ProjectManager';
import { translateProjectManger } from './ProjectManager';
import type { FinancialData, FinancialDataFR } from './FinancialData';
import { translateFinancialDataArray } from './FinancialData';
import type { DirsModel, DirsModelFR } from './DirsModel';
import { translateDirsModel } from './DirsModel';
import {
	getOperationStatusArrayFromFr,
	OperationRegulationsEnum,
	OperationStatusEnum,
	OperationStatusEnumFR
} from './enums/OperationStatusEnum';
import type { TypeVideoEmbedEnum } from './enums/TypeVideoEmbedEnum';
import type { ErrorModel } from './ErrorModel';
import type { OperationsTypeInteretsEnum } from './enums/ObligationsTypeInteretsEnum';
import type { DetailedSubscriptionStatus } from './enums/DetailedSubscriptionStatusEnum';
import { type FICIModel, type FICIResponse, translateFICIModel } from './FICIModel';
import type { DecisionToolMarks } from '@wiseed/wikit';

export type OperationResponse = {
	vote: number;
	id: number;
	nom: string;
	montantInvesti: number;
	sortie: OperationOutingsEnumFR;
	instrumentFinancier: OperationFinancialInstrumentEnumFR;
	modeleIntervention: string;
	isObligationsConvertibles: boolean;
	isOPC: boolean;
	pourcentageFrais: number;
	pourcentageTvaFrais: number;
	pourcentageFraisEntreePourWicap: number;
	pourcentageFraisGestion: number;
	pourcentPlusValueMaxTP: number;
	pourcentPlusValueMinTP: number;
	montantFraisGestion: number;
	montantMiniFraisGestion: number;
	pourcentageFraisSortie: number;
	defiscIRPP: boolean;
	defiscApportCession: boolean;
	tauxDefiscIRPP: number;
	paiementCb: boolean;
	paiementCheque: boolean;
	paiementVirementPEA: boolean;
	paiementWallet: boolean;
	ribSpecifique: FileModelFR;
	montantRequis: number;
	montantMaximumRecherche: number;
	montantRecherchePremium: number;
	montantRechercheWiseeders: number;
	montantReserveInstitutionnels: number;
	montantAtteintHorsWiseed: number;
	dateCreation: string;
	clotureCollecteDefinitive: boolean;
	dateDebutCollecte: string;
	dateDebutCollectePremium: string;
	dateFinCollecte: string;
	dateFinCollectePrevue: string;
	dateFinVote: string;
	auditEnCours: boolean;
	situationJuridique: OperationLegalStatusEnumFR;
	logo: ImageModelFR;
	imageCover: ImageModelFR;
	imageCard: ImageModelFR;
	afficherQuestionnaireFinSouscription: boolean;
	activerCommentaires: boolean;
	commentairesDesactivesConges: boolean;
	valoMin: number;
	valoMax: number;
	prixPart: number;
	primeEmission: number;
	ticketMini: number;
	ticketMax: number;
	palierSouscription: number;
	gestionFileAttente: boolean;
	ouvertureAuxVip: boolean;
	nbPartsMiniPremiereSouscription: number;
	pourcentRendementEmpruntObligataire: number;
	pourcentRendementAnnuelMinTP: number;
	pourcentRendementAnnuelMaxTP: number;
	statusConstitutif: FileModelFR;
	kbisConstitutif: FileModelFR;
	pvAgOuvertureDeCapital: FileModelFR;
	dirsPublie: boolean;
	typeCible: TargetTypeEnumFR;
	categorie: OperationCategoryEnumFR;
	typePart: ShareTypeEnumFR;
	partenariatRegion: PartnershipLabelEnumFR;
	imageEquipe: ImageModelFR;
	operateur: CompanyModelFR;
	operateur2: CompanyModelFR;
	emetteursDesTitres: CompanyModelFR;
	societeDeProjet: CompanyModelFR;
	garant1: CompanyModelFR;
	garant2: CompanyModelFR;
	garant3: CompanyModelFR;
	garant4: CompanyModelFR;
	garant5: CompanyModelFR;
	destinataireDesFonds: CompanyModelFR;
	utilisateurDesFonds: CompanyModelFR;
	scoreEnvironnement: number;
	scoreSocial: number;
	scoreGouvernance: number;
	images: ImageModelFR[];
	documents: FileModelFR[];
	nomCible: string;
	nomCibleSlug: string;
	nomEmetteursDesTitres: string;
	isCoordonneesBancairesSaisies: boolean;
	strategieSortie: string;
	descriptionOperation: string;
	descriptionWiseed: string;
	marketData: string;
	descriptionTitre: string;
	descriptionProjet: string;
	descriptionCourteProjet: string;
	vision: string;
	descriptionEquipe: string;
	analyseBonneRaison: string;
	analyseSuperiorite: string;
	analyseConcurrents: string;
	analyseProgres: string;
	fiscalite: string;
	urlSiteWeb: string;
	urlSocieteCom: string;
	urlFacebook: string;
	urlLinkedin: string;
	urlInstagram: string;
	membresEquipe: TeamMemberModelFR[];
	etapesDeveloppement: DevelopmentStepModelFR[];
	usageDesFonds: FundUsageModelFR[];
	adresseFiscale: AddressModelFR;
	localisation: string;
	scoringChiffreAffaireOperateur: string;
	scoringEndettementOperateur: string;
	scoringCapitauxPropresOperateur: string;
	scoringLiquiditePromoteur: string;
	scoringTauxReservationProgramme: string;
	scoringCouverturePrixRevient: string;
	scoringSecuriteFondsPropresWiseeders: string;
	scoringSecuriteTechnique: string;
	scoringGlobal: string;
	urlIframeHabiteoCarte: string;
	urlIframeHabiteoRendu3D: string;
	urlIframeHabiteoVisiteVirtuelle: string;
	horizonSortie: number;
	typeCiblePourFront: TargetTypeEnumFR;
	intention: number;
	offre: SubscriptionOfferModelFR;
	tranches: OperationTrancheResponse[];
	statsVotes: StatsVotesModelFR;
	intervenants: string;
	natureProduit: string;
	commercialisation: string;
	commercialisationMontageOperation: string;
	garantiesEtSuretes: string;
	structureDuFinancement: string;
	swotStrength: string;
	swotWeakness: string;
	numCadastre: string;
	numPermis: string;
	numPermisAmenager: string;
	dateObtentionPermis: string;
	datePurge: string;
	nbBatiments: number;
	nbLogements: number;
	nbGarages: number;
	nbParking: number;
	nbCommerces: number;
	surfaceHabitable: number;
	surfaceTerrain: number;
	surfaceBatiments: number;
	surfaceBureaux: number;
	surfaceActivitesAnnexes: number;
	surfaceUtile: number;
	dureeBailCommercial: string;
	tauxPrecommercialisation: number;
	tauxLotsLibres: number;
	tauxLotsSousOption: number;
	scLabelFondsPropresLeveeHolding: string;
	scMontantFondsPropresLeveeHolding: number;
	scMontantCommercialisation: number;
	chiffresCA: number;
	chiffresChargesFoncieres: number;
	chiffresCoutTravauxEtudes: number;
	chiffresFraisGeneraux: number;
	chiffresHonorairesGestion: number;
	scLabelFondsPropresCreation: string;
	scMontantFondsPropresCreation: number;
	scLabelQuasiFondsPropresLevee: string;
	scMontantQuasiFondsPropresLevee: number;
	scLabelPretBancaireDette: string;
	scMontantPretBancaireDette: number;
	structureFinancementTexte: string;
	dateEcheanceInitialeEmpruntObligataire: string;
	dateEmissionEmpruntObligataire: string;
	obligationsTypeRemboursementInterets: OperationsTypeInteretsEnum;
	gestionnaireProjet: ProjectManagerFR;
	metier: string;
	reconnaissance: string;
	partenariats: string;
	scPreMoneyFondateurs: number;
	scPreMoneyEmployes: number;
	scPreMoneyLoveMoney: number;
	scPreMoneyBusinessAngels: number;
	scPreMoneyVCs: number;
	scPreMoneyEntreprises: number;
	scPreMoneyAutres: number;
	scPreMoneyHolding: number;
	valorisation: number;
	donneesFinancieres: FinancialDataFR[];
	alerteDefiscalisation: string;
	anneeCreation: string;
	encours: string;
	secteurs: string;
	primeRemboursementPrevisionnelleTp: number;
	dateDebutInteretsTP: Date;
	dateFinPrevueInteretsTP: Date;
	dateFinReelleInteretsTP: Date;
	environnementTonnesCO2Evitees: string;
	environnementImpact: string;
	descriptionTechnique: string;
	listeActeursDeveloppementLocal: string;
	dirs: DirsModelFR;
	nbComments: number;
	nbFollowers: number;
	nbLives: number;
	nbNews: number;
	nbTestimonials: number;
	subscriptionFormSignatureUrl: string;
	statuts: OperationStatusEnumFR[];
	planningPrevisionnel: string;
	investErrors: ErrorModel[];
	clotureEnCours: boolean;
	typeVideoEmbed: TypeVideoEmbedEnum;
	idVideoEmbed: string;
	pourcentagePrimeNonConversion: number;
	immatriculationDate: string;
	taxeHabitationObligatoire: boolean;
	montantCollecte: number;
	investisseurs: number;
	pourcentageCollecte: number;
	reglementation: OperationRegulationsEnum;
	isAccesPrive: boolean;
	detailedStatus: DetailedSubscriptionStatus;
	fici: FICIResponse;
	isTauxVariable: boolean;
	triCible: number;
};

export type OperationModel = {
	id: number;
	name: string;
	amountSubscribed: number;
	outings: OperationOutingsEnum;
	financialInstrument: OperationFinancialInstrumentEnum;
	interventionModel: string;
	isConvertibleBonds: boolean;
	isOPC: boolean;
	percentageFees: number;
	percentageVATCosts: number;
	percentageCostForWicap: number;
	percentageManagementCosts: number;
	percentageCapitalgainMaxTP: number;
	percentageCapitalgainMinTP: number;
	amountManagementCosts: number;
	minAmountManagementCosts: number;
	percentageOutingsCosts: number;
	isIRPPTaxExempted: boolean;
	isDisposalContributionTaxExempted: boolean;
	IRPPTaxExemptionRate: number;
	isCreditCardPayment: boolean;
	isChequePayment: boolean;
	isPEATransferPayment: boolean;
	isWalletPayment: boolean;
	specificRIB: FileModel;
	amountNeededMin: number;
	amountNeededMax: number;
	amountNeededPremium: number;
	amountNeededWiseeders: number;
	amountReserveInstitutional: number;
	amountReachedExcludingWiseed: number;
	creationDate: string;
	isCollectDefinitelyClose: boolean;
	collectStartDate: string;
	premiumCollectStartDate: string;
	collectEndDate: string;
	estimatedCollectEndDate: string;
	voteEndDate: string;
	isVoteOngoingAudit: boolean;
	legalStatus: OperationLegalStatusEnum;
	logo: ImageModel;
	cover: ImageModel;
	card: ImageModel;
	isQuestionnaireSubscriptionEndedDisplayed: boolean;
	enableComments: boolean;
	commentsDisabledTemporary: boolean;
	valuationMin: number;
	valuationMax: number;
	oneShareAmount: number;
	primeEmission: number;
	minCoupon: number;
	maxCoupon: number;
	subscriptionLevel: number;
	isQueue: boolean;
	isOpenToVIP: boolean;
	nbMinimumFirstSubscriptionShare: number;
	percentBondYield: number;
	percentAnnualPerformanceMinES: number;
	percentAnnualPerformanceMaxES: number;
	constitutiveStatus: FileModel;
	constitutiveKbis: FileModel;
	capitalOpeningAGReport: FileModel;
	isDirsPublished: boolean;
	targetType: TargetTypeEnum;
	category: OperationCategoryEnum | undefined;
	shareType: ShareTypeEnum;
	partnershipLabel: PartnershipLabelEnum;
	teamPhotos: ImageModel;
	operator: CompanyModel;
	operator2: CompanyModel;
	sharesTransmitters: CompanyModel;
	projectCompany: CompanyModel;
	guarantor1: CompanyModel;
	guarantor2: CompanyModel;
	guarantor3: CompanyModel;
	guarantor4: CompanyModel;
	guarantor5: CompanyModel;
	recipientOfFunds: CompanyModel;
	userOfFunds: CompanyModel;
	environmentScore: number;
	socialScore: number;
	governanceScore: number;
	images: ImageModel[];
	documents: FileModel[];
	targetName: string;
	targetNameSlug: string;
	sharesTransmittersName: string;
	isBankInfoEntered: boolean;
	outingsStrategy: string;
	description: string;
	descriptionWiseed: string;
	marketData: string;
	projectDescriptionTitle: string;
	projectDescription: string;
	shortProjectDescription: string;
	vision: string;
	teamDescription: string;
	commentWhyInvest: string;
	commentSuperiority: string;
	commentConcurrency: string;
	commentProgression: string;
	localization: string;
	taxation: string;
	websiteUrl: string;
	societeComUrl: string;
	facebookUrl: string;
	linkedInUrl: string;
	instagramUrl: string;
	teamMembers: TeamMemberModel[];
	developmentSteps: DevelopmentStepModel[];
	fundUsages: FundUsageModel[];
	address: AddressModel;
	scoringRevenue: DecisionToolMarks;
	scoringDept: DecisionToolMarks;
	scoringEquity: DecisionToolMarks;
	scoringLiquidity: DecisionToolMarks;
	scoringReservationRate: DecisionToolMarks;
	scoringCoverageCostPrice: DecisionToolMarks;
	scoringSecurityOwnFunds: DecisionToolMarks;
	scoringTechnicalSecurity: DecisionToolMarks;
	scoringGlobal: string;
	urlIframeMap: string;
	urlIframe3DRender: string;
	urlIframeVirtualVisit: string;
	outingsHorizon: number;
	intention: number;
	offer: SubscriptionOfferModel;
	tranches: OperationTrancheModel[];
	statsVotes: StatsVotesModel;
	stakeholder: string;
	productNature: string;
	commercialisation: string;
	commercialisationMontageOperation: string;
	guaranteesAndSureties: string;
	structureDuFinancement: string;
	swotStrength: string;
	swotWeakness: string;
	numCadastre: string;
	numPermis: string;
	numPermisAmenager: string;
	dateObtentionPermis: string;
	datePurge: string;
	nbBatiments: number;
	nbLogements: number;
	nbGarages: number;
	nbParking: number;
	nbCommerces: number;
	surfaceHabitable: number;
	surfaceTerrain: number;
	surfaceBatiments: number;
	surfaceBureaux: number;
	surfaceActivitesAnnexes: number;
	surfaceUtile: number;
	dureeBailCommercial: string;
	tauxPrecommercialisation: number;
	tauxLotsLibres: number;
	tauxLotsSousOption: number;
	scLabelFondsPropresLeveeHolding: string;
	scMontantFondsPropresLeveeHolding: number;
	scMontantCommercialisation: number;
	chiffresCA: number;
	chiffresChargesFoncieres: number;
	chiffresCoutTravauxEtudes: number;
	chiffresFraisGeneraux: number;
	chiffresHonorairesGestion: number;
	scLabelFondsPropresCreation: string;
	scMontantFondsPropresCreation: number;
	scLabelQuasiFondsPropresLevee: string;
	scMontantQuasiFondsPropresLevee: number;
	scLabelPretBancaireDette: string;
	scMontantPretBancaireDette: number;
	structureFinancementTexte: string;
	dateEcheanceInitialeEmpruntObligataire: string;
	dateEmissionEmpruntObligataire: string;
	obligationsTypeRemboursementInterets: OperationsTypeInteretsEnum;
	gestionnaireProjet: ProjectManager;
	metier: string;
	reconnaissance: string;
	partenariats: string;
	scPreMoneyFondateurs: number;
	scPreMoneyEmployes: number;
	scPreMoneyLoveMoney: number;
	scPreMoneyBusinessAngels: number;
	scPreMoneyVCs: number;
	scPreMoneyEntreprises: number;
	scPreMoneyAutres: number;
	scPreMoneyHolding: number;
	valorisation: number;
	financialData: FinancialData[];
	alerteDefiscalisation: string;
	anneeCreation: string;
	encours: string;
	secteurs: string;
	primeRemboursementPrevisionnelleTp: number;
	dateDebutInteretsTP: Date;
	dateFinPrevueInteretsTP: Date;
	dateFinReelleInteretsTP: Date;
	environnementTonnesCO2Evitees: string;
	environnementImpact: string;
	descriptionTechnique: string;
	listeActeursDeveloppementLocal: string;
	dirs: DirsModel;
	nbComments: number;
	nbFollowers: number;
	nbLives: number;
	nbNews: number;
	nbTestimonials: number;
	subscriptionFormSignatureUrl: string;
	status: OperationStatusEnum[];
	planningPrevisionnel: string;
	investErrors: ErrorModel[];
	clotureEnCours: boolean;
	typeVideoEmbed: TypeVideoEmbedEnum;
	idVideoEmbed: string;
	userVote: number;
	pnc: number;
	immatriculationDate: string;
	mandatoryHousingTax: boolean;
	amountCollected: number;
	nbOfInvestors: number;
	percentageCollected: number;
	regulation: OperationRegulationsEnum;
	isPrivateAccess: boolean;
	detailedStatus: DetailedSubscriptionStatus;
	fici: FICIModel;
	isVariableRate: boolean;
	triCible: number;
};

export const translateOperationResponse = (o: OperationResponse): OperationModel => {
	return {
		id: o.id,
		name: o.nom,
		amountSubscribed: o.montantInvesti,
		outings: o.sortie && translateOperationOutings(o.sortie),
		financialInstrument:
			o.instrumentFinancier && getOperationFinancialInstrumentFromFR(o.instrumentFinancier),
		interventionModel: o.modeleIntervention,
		isConvertibleBonds: o.isObligationsConvertibles,
		isOPC: o.isOPC,
		percentageFees: o.pourcentageFrais,
		percentageVATCosts: o.pourcentageTvaFrais,
		percentageCostForWicap: o.pourcentageFraisEntreePourWicap,
		percentageManagementCosts: o.pourcentageFraisGestion,
		percentageCapitalgainMaxTP: o.pourcentPlusValueMaxTP,
		percentageCapitalgainMinTP: o.pourcentPlusValueMinTP,
		amountManagementCosts: o.montantFraisGestion,
		minAmountManagementCosts: o.montantMiniFraisGestion,
		percentageOutingsCosts: o.pourcentageFraisSortie,
		isIRPPTaxExempted: o.defiscIRPP,
		isDisposalContributionTaxExempted: o.defiscApportCession,
		IRPPTaxExemptionRate: o.tauxDefiscIRPP,
		isCreditCardPayment: o.paiementCb,
		isChequePayment: o.paiementCheque,
		isPEATransferPayment: o.paiementVirementPEA,
		isWalletPayment: o.paiementWallet,
		specificRIB: o.ribSpecifique && translateFileModel(o.ribSpecifique),
		amountNeededMin: o.montantRequis,
		amountNeededMax: o.montantMaximumRecherche,
		amountNeededPremium: o.montantRecherchePremium,
		amountNeededWiseeders: o.montantRechercheWiseeders,
		amountReserveInstitutional: o.montantReserveInstitutionnels,
		amountReachedExcludingWiseed: o.montantAtteintHorsWiseed,
		creationDate: o.dateCreation,
		isCollectDefinitelyClose: o.clotureCollecteDefinitive,
		collectStartDate: o.dateDebutCollecte,
		premiumCollectStartDate: o.dateDebutCollectePremium,
		collectEndDate: o.dateFinCollecte,
		estimatedCollectEndDate: o.dateFinCollectePrevue,
		voteEndDate: o.dateFinVote,
		isVoteOngoingAudit: o.auditEnCours,
		legalStatus: o.situationJuridique && translateOperationLegalStatus(o.situationJuridique),
		logo: o.logo && translateImageModel(o.logo),
		cover: o.imageCover && translateImageModel(o.imageCover),
		isQuestionnaireSubscriptionEndedDisplayed: o.afficherQuestionnaireFinSouscription,
		enableComments: o.activerCommentaires,
		commentsDisabledTemporary: o.commentairesDesactivesConges,
		valuationMin: o.valoMin,
		valuationMax: o.valoMax,
		oneShareAmount: o.prixPart,
		primeEmission: o.primeEmission,
		minCoupon: o.ticketMini,
		maxCoupon: o.ticketMax,
		subscriptionLevel: o.palierSouscription,
		isQueue: o.gestionFileAttente,
		isOpenToVIP: o.ouvertureAuxVip,
		nbMinimumFirstSubscriptionShare: o.nbPartsMiniPremiereSouscription,
		percentBondYield: o.pourcentRendementEmpruntObligataire,
		percentAnnualPerformanceMinES: o.pourcentRendementAnnuelMinTP,
		percentAnnualPerformanceMaxES: o.pourcentRendementAnnuelMaxTP,
		constitutiveStatus: o.statusConstitutif && translateFileModel(o.statusConstitutif),
		constitutiveKbis: o.kbisConstitutif && translateFileModel(o.kbisConstitutif),
		capitalOpeningAGReport:
			o.pvAgOuvertureDeCapital && translateFileModel(o.pvAgOuvertureDeCapital),
		isDirsPublished: o.dirsPublie,
		targetType: o.typeCible && getTargetTypeFromFR(o.typeCible),
		category: o.categorie && getOperationCategoryFromFR(o.categorie),
		shareType: o.typePart && translateShareTypeEnum(o.typePart),
		partnershipLabel: o.partenariatRegion && getPartnershipLabelFromFR(o.partenariatRegion),
		teamPhotos: o.imageEquipe && translateImageModel(o.imageEquipe),
		operator: o.operateur && translateCompany(o.operateur),
		operator2: o.operateur2 && translateCompany(o.operateur2),
		sharesTransmitters: o.emetteursDesTitres && translateCompany(o.emetteursDesTitres),
		projectCompany: o.societeDeProjet && translateCompany(o.societeDeProjet),
		guarantor1: o.garant1 && translateCompany(o.garant1),
		guarantor2: o.garant2 && translateCompany(o.garant2),
		guarantor3: o.garant3 && translateCompany(o.garant3),
		guarantor4: o.garant4 && translateCompany(o.garant4),
		guarantor5: o.garant5 && translateCompany(o.garant5),
		recipientOfFunds: o.destinataireDesFonds && translateCompany(o.destinataireDesFonds),
		userOfFunds: o.utilisateurDesFonds && translateCompany(o.utilisateurDesFonds),
		environmentScore: o.scoreEnvironnement,
		socialScore: o.scoreSocial,
		governanceScore: o.scoreGouvernance,
		images: o.images && translateImageModelArray(o.images),
		documents: o.documents && translateFileModelArray(o.documents),
		targetName: o.nomCible,
		targetNameSlug: o.nomCibleSlug,
		sharesTransmittersName: o.nomEmetteursDesTitres,
		isBankInfoEntered: o.isCoordonneesBancairesSaisies,
		outingsStrategy: o.strategieSortie,
		description: o.descriptionOperation,
		descriptionWiseed: o.descriptionWiseed,
		marketData: o.marketData,
		projectDescriptionTitle: o.descriptionTitre,
		projectDescription: o.descriptionProjet,
		shortProjectDescription: o.descriptionCourteProjet,
		vision: o.vision,
		teamDescription: o.descriptionEquipe,
		commentWhyInvest: o.analyseBonneRaison,
		commentSuperiority: o.analyseSuperiorite,
		commentConcurrency: o.analyseConcurrents,
		commentProgression: o.analyseProgres,
		taxation: o.fiscalite,
		websiteUrl: o.urlSiteWeb,
		societeComUrl: o.urlSocieteCom,
		facebookUrl: o.urlFacebook,
		linkedInUrl: o.urlLinkedin,
		instagramUrl: o.urlInstagram,
		nbFollowers: o.nbFollowers,
		teamMembers: o.membresEquipe ? translateTeamMemberArray(o.membresEquipe) : [],
		developmentSteps: o.etapesDeveloppement
			? translateDevelopmentStepArray(o.etapesDeveloppement)
			: [],
		fundUsages: o.usageDesFonds ? translateFundUsageModelArray(o.usageDesFonds) : [],
		address: o.adresseFiscale && translateAddressModel(o.adresseFiscale),
		scoringRevenue: o.scoringChiffreAffaireOperateur,
		scoringDept: o.scoringEndettementOperateur,
		scoringEquity: o.scoringCapitauxPropresOperateur,
		scoringLiquidity: o.scoringLiquiditePromoteur,
		scoringReservationRate: o.scoringTauxReservationProgramme,
		scoringCoverageCostPrice: o.scoringCouverturePrixRevient,
		scoringSecurityOwnFunds: o.scoringSecuriteFondsPropresWiseeders,
		scoringTechnicalSecurity: o.scoringSecuriteTechnique,
		scoringGlobal: o.scoringGlobal,
		urlIframeMap: o.urlIframeHabiteoCarte,
		urlIframe3DRender: o.urlIframeHabiteoRendu3D,
		urlIframeVirtualVisit: o.urlIframeHabiteoVisiteVirtuelle,
		localization: o.localisation,
		outingsHorizon: o.horizonSortie,
		intention: o.intention,
		offer: o.offre && translateSubscriptionOfferModel(o.offre),
		tranches: o.tranches && translateOperationTrancheResponseArray(o.tranches),
		statsVotes: o.statsVotes && translateStatsVotesModel(o.statsVotes),
		stakeholder: o.intervenants,
		productNature: o.natureProduit,
		commercialisation: o.commercialisation,
		commercialisationMontageOperation: o.commercialisationMontageOperation,
		guaranteesAndSureties: o.garantiesEtSuretes,
		structureDuFinancement: o.structureDuFinancement,
		swotStrength: o.swotStrength,
		swotWeakness: o.swotWeakness,
		numCadastre: o.numCadastre,
		numPermis: o.numPermis,
		numPermisAmenager: o.numPermisAmenager,
		dateObtentionPermis: o.dateObtentionPermis,
		datePurge: o.datePurge,
		nbBatiments: o.nbBatiments,
		nbLogements: o.nbLogements,
		nbGarages: o.nbGarages,
		nbParking: o.nbParking,
		nbCommerces: o.nbCommerces,
		surfaceHabitable: o.surfaceHabitable,
		surfaceTerrain: o.surfaceTerrain,
		surfaceBatiments: o.surfaceBatiments,
		surfaceBureaux: o.surfaceBureaux,
		surfaceActivitesAnnexes: o.surfaceActivitesAnnexes,
		surfaceUtile: o.surfaceUtile,
		dureeBailCommercial: o.dureeBailCommercial,
		tauxPrecommercialisation: o.tauxPrecommercialisation,
		tauxLotsLibres: o.tauxLotsLibres,
		tauxLotsSousOption: o.tauxLotsSousOption,
		scLabelFondsPropresLeveeHolding: o.scLabelFondsPropresLeveeHolding,
		scMontantFondsPropresLeveeHolding: o.scMontantFondsPropresLeveeHolding,
		scMontantCommercialisation: o.scMontantCommercialisation,
		chiffresCA: o.chiffresCA,
		chiffresChargesFoncieres: o.chiffresChargesFoncieres,
		chiffresCoutTravauxEtudes: o.chiffresCoutTravauxEtudes,
		chiffresFraisGeneraux: o.chiffresFraisGeneraux,
		chiffresHonorairesGestion: o.chiffresHonorairesGestion,
		scLabelFondsPropresCreation: o.scLabelFondsPropresCreation,
		scMontantFondsPropresCreation: o.scMontantFondsPropresCreation,
		scLabelQuasiFondsPropresLevee: o.scLabelQuasiFondsPropresLevee,
		scMontantQuasiFondsPropresLevee: o.scMontantQuasiFondsPropresLevee,
		scLabelPretBancaireDette: o.scLabelPretBancaireDette,
		scMontantPretBancaireDette: o.scMontantPretBancaireDette,
		structureFinancementTexte: o.structureFinancementTexte,
		dateEmissionEmpruntObligataire: o.dateEmissionEmpruntObligataire,
		dateEcheanceInitialeEmpruntObligataire: o.dateEcheanceInitialeEmpruntObligataire,
		obligationsTypeRemboursementInterets: o.obligationsTypeRemboursementInterets,
		gestionnaireProjet: o.gestionnaireProjet && translateProjectManger(o.gestionnaireProjet),
		metier: o.metier,
		reconnaissance: o.reconnaissance,
		partenariats: o.partenariats,
		scPreMoneyFondateurs: o.scPreMoneyFondateurs,
		scPreMoneyEmployes: o.scPreMoneyEmployes,
		scPreMoneyLoveMoney: o.scPreMoneyLoveMoney,
		scPreMoneyBusinessAngels: o.scPreMoneyBusinessAngels,
		scPreMoneyVCs: o.scPreMoneyVCs,
		scPreMoneyEntreprises: o.scPreMoneyEntreprises,
		scPreMoneyAutres: o.scPreMoneyAutres,
		scPreMoneyHolding: o.scPreMoneyHolding,
		valorisation: o.valorisation,
		financialData: o.donneesFinancieres && translateFinancialDataArray(o.donneesFinancieres),
		alerteDefiscalisation: o.alerteDefiscalisation,
		anneeCreation: o.anneeCreation,
		encours: o.encours,
		secteurs: o.secteurs,
		primeRemboursementPrevisionnelleTp: o.primeRemboursementPrevisionnelleTp,
		dateDebutInteretsTP: o.dateDebutInteretsTP,
		dateFinPrevueInteretsTP: o.dateFinPrevueInteretsTP,
		dateFinReelleInteretsTP: o.dateFinReelleInteretsTP,
		environnementTonnesCO2Evitees: o.environnementTonnesCO2Evitees,
		environnementImpact: o.environnementImpact,
		descriptionTechnique: o.descriptionTechnique,
		listeActeursDeveloppementLocal: o.listeActeursDeveloppementLocal,
		dirs: o.dirs && translateDirsModel(o.dirs),
		nbComments: o.nbComments,
		nbLives: o.nbLives,
		nbNews: o.nbNews,
		nbTestimonials: o.nbTestimonials,
		subscriptionFormSignatureUrl: o.subscriptionFormSignatureUrl,
		status: o.statuts && getOperationStatusArrayFromFr(o.statuts),
		planningPrevisionnel: o.planningPrevisionnel,
		investErrors: o.investErrors,
		clotureEnCours: o.clotureEnCours,
		typeVideoEmbed: o.typeVideoEmbed,
		idVideoEmbed: o.idVideoEmbed,
		userVote: o.vote,
		pnc: o.pourcentagePrimeNonConversion,
		immatriculationDate: o.immatriculationDate,
		mandatoryHousingTax: o.taxeHabitationObligatoire,
		amountCollected: o.montantCollecte,
		nbOfInvestors: o.investisseurs,
		percentageCollected: o.pourcentageCollecte,
		regulation: o.reglementation,
		isPrivateAccess: o.isAccesPrive,
		detailedStatus: o.detailedStatus,
		card: o.imageCard && translateImageModel(o.imageCard),
		fici: o.fici && translateFICIModel(o.fici),
		isVariableRate: o.isTauxVariable,
		triCible: o.triCible
	};
};
export interface AdaptedOperationModel {
	id: string;
	startDate: string;
	projectName: string;
	projectTypeTarget?: string;
	projectShortDescriptionFr?: string;
	isPremiumOnly?: boolean;
	projectShortImageCardURL: string;
	amountNeeded: number;
	amountSubscribed?: number;
	amountNeededWiseeders?: number;
	amountNeededPremium?: number;
	amountReserveInstitutional?: number;
	percentageYieldBond: number;
	nbOfInvestors?: number;
	follows?: boolean;
	isPrivateAccess?: boolean;
	category?: string;
	amountReachedExcludingWiseed?: number;
	outingsHorizon?: number;
	financialInstrument?: OperationFinancialInstrumentEnum;
	isOngoingClosing: boolean;
	isCollectComing?: boolean;
	collectEndDate: string;
	isCollectEnded?: boolean;
	isCollectAlmostEnd?: boolean;
	amountCollected?: number;
	amountNeededMin?: number;
	isCollectRunning?: boolean;
}
